import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { STATE_NAMES, SUPPORTED_STATES } from '@shared/counties/index.js';
import { CountyPicker } from '@/components/CountyPicker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { PIPELINE_STATUSES } from '@shared/enums.js';
import type { LeadDetail } from '@shared/types.js';

interface FormState {
  businessName: string;
  legalName: string;
  ownerName: string;
  contactName: string;
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  primaryCategoryId: string;
  countyId: string;
  googleBusinessProfileUrl: string;
  facebookUrl: string;
  googlePlaceId: string;
  rating: string;
  reviewCount: string;
  outreachAngle: string;
  researchNotes: string;
  pipelineStatus: string;
  /**
   * Held as the text that was actually established: "2019", "2019-06" or
   * "2019-06-14". Never a date-input value, because a date input can only
   * express a known day and would turn "some time in 2019" into 1 January 2019.
   */
  businessStartedDate: string;
  businessStartedPrecision: '' | 'day' | 'month' | 'year';
  serviceCountyIds: number[];
}

/** How a start date is written at each precision. */
const START_DATE_FORMAT: Record<'day' | 'month' | 'year', { placeholder: string; hint: string }> = {
  day: { placeholder: '2019-06-14', hint: 'Exact date, YYYY-MM-DD.' },
  month: { placeholder: '2019-06', hint: 'Month and year only, YYYY-MM. Stored as approximate.' },
  year: { placeholder: '2019', hint: 'Year only, YYYY. Stored as approximate.' },
};

/**
 * Recover the precision the stored value really has.
 *
 * A month- or year-only date is anchored internally on the first of the period,
 * so "2019-01-01" with year precision must be shown as "2019" — otherwise the
 * form invites someone to confirm a day nobody ever verified.
 */
function displayStartDate(date: string | null, precision: string | null): string {
  if (!date) return '';
  if (precision === 'year') return date.slice(0, 4);
  if (precision === 'month') return date.slice(0, 7);
  return date;
}

/** Infer precision from what was typed, so the two can never disagree. */
function inferPrecision(value: string): 'day' | 'month' | 'year' | null {
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return 'day';
  if (/^\d{4}-\d{2}$/.test(v)) return 'month';
  if (/^\d{4}$/.test(v)) return 'year';
  return null;
}

/** Expand a partial date to the storable YYYY-MM-DD anchor. */
function anchoredStartDate(value: string): string | null {
  const v = value.trim();
  const precision = inferPrecision(v);
  if (!precision) return null;
  if (precision === 'day') return v;
  if (precision === 'month') return `${v}-01`;
  return `${v}-01-01`;
}

const EMPTY: FormState = {
  businessName: '',
  legalName: '',
  ownerName: '',
  contactName: '',
  primaryPhone: '',
  secondaryPhone: '',
  email: '',
  streetAddress: '',
  city: '',
  // Overridden from general.settings.defaultState when the dialog opens.
  state: SUPPORTED_STATES[0],
  zipCode: '',
  primaryCategoryId: '',
  countyId: '',
  googleBusinessProfileUrl: '',
  facebookUrl: '',
  googlePlaceId: '',
  rating: '',
  reviewCount: '',
  outreachAngle: '',
  researchNotes: '',
  pipelineStatus: 'New',
  businessStartedDate: '',
  businessStartedPrecision: '',
  serviceCountyIds: [],
};

function fromLead(detail: LeadDetail): FormState {
  const l = detail.lead;
  return {
    businessName: l.businessName,
    legalName: l.legalName ?? '',
    ownerName: l.ownerName ?? '',
    contactName: l.contactName ?? '',
    primaryPhone: l.primaryPhone ?? '',
    secondaryPhone: l.secondaryPhone ?? '',
    email: l.email ?? '',
    streetAddress: l.streetAddress ?? '',
    city: l.city ?? '',
    state: l.state ?? SUPPORTED_STATES[0],
    zipCode: l.zipCode ?? '',
    primaryCategoryId: l.primaryCategoryId ? String(l.primaryCategoryId) : '',
    countyId: l.countyId ? String(l.countyId) : '',
    googleBusinessProfileUrl: l.googleBusinessProfileUrl ?? '',
    facebookUrl: l.facebookUrl ?? '',
    googlePlaceId: l.googlePlaceId ?? '',
    rating: l.rating !== null ? String(l.rating) : '',
    reviewCount: l.reviewCount !== null ? String(l.reviewCount) : '',
    outreachAngle: l.outreachAngle ?? '',
    researchNotes: l.researchNotes ?? '',
    pipelineStatus: l.pipelineStatus,
    businessStartedDate: displayStartDate(l.businessStartedDate, l.businessStartedPrecision),
    businessStartedPrecision:
      (l.businessStartedPrecision as 'day' | 'month' | 'year' | null) ??
      (l.businessStartedDate ? 'day' : ''),
    serviceCountyIds: detail.serviceCounties.map((c) => c.id),
  };
}

/** general.settings.defaultState, falling back to the first supported state. */
function useDefaultState(enabled: boolean): string {
  // /api/settings is admin-only. Fetching it whenever the Leads page rendered
  // produced a background 403 for every VA, for a value only a lead-creating
  // admin can use. Gated on the dialog actually being open to an admin.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: api.settings,
    staleTime: 300_000,
    enabled,
  });
  const general = settings.data?.settings['general.settings'] as { defaultState?: string } | undefined;
  const configured = general?.defaultState;
  return configured && (SUPPORTED_STATES as readonly string[]).includes(configured)
    ? configured
    : SUPPORTED_STATES[0];
}

export function LeadFormDialog({
  open,
  onOpenChange,
  lead,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead?: LeadDetail;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [error, setError] = React.useState<string | null>(null);

  const { user, authEnabled } = useAuth();
  // A VA cannot create or edit leads, so nothing here needs the admin setting.
  const canUseForm = !authEnabled || user?.role === 'admin';
  const defaultState = useDefaultState(open && canUseForm);

  React.useEffect(() => {
    if (open) {
      // A new lead starts in the configured default state; an existing lead
      // keeps whatever it already has.
      setForm(lead ? fromLead(lead) : { ...EMPTY, state: defaultState });
      setError(null);
    }
  }, [open, lead, defaultState]);

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories, enabled: open });
  const counties = useQuery({ queryKey: ['counties'], queryFn: api.counties, enabled: open });

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const payload = () => ({
    businessName: form.businessName.trim(),
    legalName: form.legalName.trim() || null,
    ownerName: form.ownerName.trim() || null,
    contactName: form.contactName.trim() || null,
    primaryPhone: form.primaryPhone.trim() || null,
    secondaryPhone: form.secondaryPhone.trim() || null,
    email: form.email.trim() || null,
    streetAddress: form.streetAddress.trim() || null,
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    zipCode: form.zipCode.trim() || null,
    primaryCategoryId: form.primaryCategoryId ? Number(form.primaryCategoryId) : null,
    countyId: form.countyId ? Number(form.countyId) : null,
    googleBusinessProfileUrl: form.googleBusinessProfileUrl.trim() || null,
    facebookUrl: form.facebookUrl.trim() || null,
    googlePlaceId: form.googlePlaceId.trim() || null,
    rating: form.rating ? Number(form.rating) : null,
    reviewCount: form.reviewCount ? Number(form.reviewCount) : null,
    outreachAngle: form.outreachAngle.trim() || null,
    researchNotes: form.researchNotes.trim() || null,
    pipelineStatus: form.pipelineStatus,
    // The API takes a full YYYY-MM-DD, so a partial value is anchored on the
    // first of the period — and the precision travels with it so nothing
    // downstream ever renders that anchor as a verified day.
    businessStartedDate: anchoredStartDate(form.businessStartedDate),
    businessStartedPrecision: inferPrecision(form.businessStartedDate),
    serviceCountyIds: form.serviceCountyIds,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (lead) {
        await api.updateLead(lead.lead.id, payload());
        return lead.lead.id;
      }
      const created = await api.createLead(payload());
      return created.id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(lead ? 'Lead updated' : 'Lead created');
      onOpenChange(false);
      if (!lead) navigate(`/leads/${id}`);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError && err.code === 'place_id_conflict'
          ? `${err.message}. Clear the Place ID here, or edit the other lead.`
          : (err as Error).message;
      setError(message);
      toast.error(message);
    },
  });

  const canSave = form.businessName.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{lead ? `Edit ${lead.lead.businessName}` : 'New lead'}</DialogTitle>
          <DialogDescription>
            Phone numbers and business names are normalized on save so duplicate detection can compare them.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          <section className="grid grid-cols-2 gap-3">
            <Field label="Business name" className="col-span-2">
              <Input value={form.businessName} onChange={set('businessName')} autoFocus />
            </Field>
            <Field label="Legal name">
              <Input value={form.legalName} onChange={set('legalName')} />
            </Field>
            <Field label="Pipeline status">
              <Select value={form.pipelineStatus} onChange={set('pipelineStatus')}>
                {PIPELINE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <Field label="Owner name">
              <Input value={form.ownerName} onChange={set('ownerName')} />
            </Field>
            <Field label="Contact name">
              <Input value={form.contactName} onChange={set('contactName')} />
            </Field>
            <Field label="Primary phone">
              <Input value={form.primaryPhone} onChange={set('primaryPhone')} placeholder="(937) 555-0123" />
            </Field>
            <Field label="Secondary phone">
              <Input value={form.secondaryPhone} onChange={set('secondaryPhone')} />
            </Field>
            <Field label="Email" className="col-span-2">
              <Input value={form.email} onChange={set('email')} type="email" />
            </Field>
          </section>

          <section className="grid grid-cols-4 gap-3">
            <Field label="Street address" className="col-span-4">
              <Input value={form.streetAddress} onChange={set('streetAddress')} />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={set('city')} />
            </Field>
            <Field label="County">
              <CountyPicker
                counties={counties.data}
                value={form.countyId ? Number(form.countyId) : null}
                onChange={(id) => {
                  // Choosing a county fixes the state: the county knows which
                  // one it is in, and the server rejects the mismatch anyway.
                  const county = counties.data?.find((c) => c.id === id);
                  setForm((f) => ({
                    ...f,
                    countyId: id ? String(id) : '',
                    state: county?.state ?? f.state,
                  }));
                }}
                placeholder="—"
              />
            </Field>
            <Field label="State">
              {/* Michigan is selectable; the default comes from settings. */}
              <Select
                value={form.state}
                onChange={(e) => {
                  const nextState = e.target.value;
                  setForm((f) => {
                    const county = counties.data?.find((c) => String(c.id) === f.countyId);
                    // A physical county in the old state is now wrong, so it is
                    // cleared. SERVICE counties are left alone: an Ohio business
                    // may legitimately serve Michigan counties.
                    const keepCounty = !county || county.state === nextState;
                    return { ...f, state: nextState, countyId: keepCounty ? f.countyId : '' };
                  });
                }}
              >
                {SUPPORTED_STATES.map((code) => (
                  <option key={code} value={code}>
                    {STATE_NAMES[code]} ({code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ZIP">
              <Input value={form.zipCode} onChange={set('zipCode')} />
            </Field>
            <Field label="Counties served" className="col-span-4">
              <CountyPicker
                multiple
                counties={counties.data}
                value={form.serviceCountyIds}
                onChange={(ids) => setForm((f) => ({ ...f, serviceCountyIds: ids }))}
                placeholder="None recorded"
                hint="Counties this business will travel to. The county above is where it is based."
              />
            </Field>
          </section>

          <section className="grid grid-cols-3 gap-3">
            <Field label="Category">
              <Select value={form.primaryCategoryId} onChange={set('primaryCategoryId')}>
                <option value="">—</option>
                {categories.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Rating">
              <Input value={form.rating} onChange={set('rating')} type="number" step="0.1" min="0" max="5" />
            </Field>
            <Field label="Review count">
              <Input value={form.reviewCount} onChange={set('reviewCount')} type="number" min="0" />
            </Field>
          </section>

          {/*
            Start date, precision first.

            This was a date input, which can only express a known day. A
            business known to have started "some time in 2019" was therefore
            stored, and shown back, as 14 June-style exactness it never had.
            Choosing the precision first decides what may be typed, and the
            precision is submitted with the value so nothing downstream has to
            guess.
          */}
          <section className="grid grid-cols-3 gap-3">
            <Field label="Start date precision" hint="How exactly is this known?">
              <Select
                value={form.businessStartedPrecision}
                onChange={(e) => {
                  const precision = e.target.value as FormState['businessStartedPrecision'];
                  setForm((f) => ({
                    ...f,
                    businessStartedPrecision: precision,
                    // Re-cut the value to the new precision instead of leaving
                    // "2019-06-14" sitting under a "year only" label.
                    businessStartedDate: !precision
                      ? ''
                      : precision === 'year'
                        ? f.businessStartedDate.slice(0, 4)
                        : precision === 'month'
                          ? f.businessStartedDate.slice(0, 7)
                          : f.businessStartedDate,
                  }));
                }}
              >
                <option value="">Not known</option>
                <option value="day">Exact date</option>
                <option value="month">Month and year</option>
                <option value="year">Year only</option>
              </Select>
            </Field>
            <Field
              label="Business start date"
              className="col-span-2"
              hint={
                form.businessStartedPrecision
                  ? `${START_DATE_FORMAT[form.businessStartedPrecision].hint} Newer businesses score higher.`
                  : 'Choose a precision first.'
              }
            >
              <Input
                value={form.businessStartedDate}
                onChange={set('businessStartedDate')}
                disabled={!form.businessStartedPrecision}
                placeholder={
                  form.businessStartedPrecision
                    ? START_DATE_FORMAT[form.businessStartedPrecision].placeholder
                    : ''
                }
                inputMode="numeric"
              />
            </Field>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <Field label="Google Business Profile URL">
              <Input value={form.googleBusinessProfileUrl} onChange={set('googleBusinessProfileUrl')} />
            </Field>
            <Field label="Facebook URL">
              <Input value={form.facebookUrl} onChange={set('facebookUrl')} />
            </Field>
            <Field
              label="Google Place ID"
              hint="Must be unique across active leads."
              className="col-span-2"
            >
              <Input value={form.googlePlaceId} onChange={set('googlePlaceId')} />
            </Field>
          </section>

          <section className="space-y-3">
            <Field label="Outreach angle">
              <Textarea
                value={form.outreachAngle}
                onChange={set('outreachAngle')}
                rows={2}
                placeholder="What to lead with on the call"
              />
            </Field>
            <Field label="Research notes">
              <Textarea value={form.researchNotes} onChange={set('researchNotes')} rows={3} />
            </Field>
          </section>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
            {lead ? 'Save changes' : 'Create lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
